import { useState, useMemo } from "react";
import { Link } from "react-router";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { useDealErrors } from "src/dm/hooks/useDealActivity";

// Build the "Reference" deep-link for an error row — legacy renders a
// per-row hyperlink that jumps to the offending field (ViewItem, ViewFees,
// NewDeal/DealHeader). We mirror that here.
const buildReference = (
  dealId: number,
  row: { dealItemId: number | null; actionEvent: string | null; refLink?: string | null }
): { label: string; to: string } | null => {
  if (row.dealItemId != null && row.dealItemId !== 0) {
    return { label: `Item ${row.dealItemId}`, to: `/dm/deals/${dealId}/items/${row.dealItemId}` };
  }
  if (row.actionEvent?.toLowerCase().includes("fee")) {
    return { label: "Fees", to: `/dm/deals/${dealId}/fees` };
  }
  if (row.actionEvent?.toLowerCase().includes("note")) {
    return { label: "Notes", to: `/dm/deals/${dealId}/notes` };
  }
  return { label: "Deal", to: `/dm/deals/${dealId}` };
};

const DealErrorsPage = () => {
  const deal = useDealContext();
  const { data, isLoading, isError, error } = useDealErrors(deal.dealId);
  const [displayWarnings, setDisplayWarnings] = useState(true);

  // Filter by severity — legacy "Display Warnings" checkbox hides W rows
  // when unchecked, keeps E (errors).
  const filteredRows = useMemo(() => {
    if (!data) return [];
    return data.filter((r) => {
      const sev = (r.severity ?? "").toUpperCase();
      if (sev === "W") return displayWarnings;
      return true;
    });
  }, [data, displayWarnings]);

  const ReferenceCell = (props: GridCellProps) => {
    const row = props.dataItem as { dealItemId: number | null; actionEvent: string | null; refLink?: string | null };
    const ref = buildReference(deal.dealId, row);
    return (
      <td>
        {ref ? (
          <Link to={ref.to} style={{ color: "#0a3d62", textDecoration: "none" }}>
            {ref.label} →
          </Link>
        ) : (
          "—"
        )}
      </td>
    );
  };

  if (isLoading) return <p>Loading errors…</p>;
  if (isError)
    return (
      <p style={{ color: "#b91c1c" }}>
        {error instanceof Error ? error.message : "Failed to load"}
      </p>
    );

  const total = data?.length ?? 0;
  const warningCount = data?.filter((r) => (r.severity ?? "").toUpperCase() === "W").length ?? 0;
  const errorCount = total - warningCount;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
        <h2 style={{ margin: 0 }}>Warnings &amp; Errors</h2>
        <Checkbox
          label="Display Warnings"
          checked={displayWarnings}
          onChange={(e) => setDisplayWarnings(!!e.value)}
        />
      </div>

      <p style={{ color: "#475569", fontSize: "0.85rem", marginTop: 0 }}>
        {errorCount} error{errorCount === 1 ? "" : "s"} · {warningCount} warning
        {warningCount === 1 ? "" : "s"} · showing {filteredRows.length}
      </p>

      {filteredRows.length === 0 ? (
        <p style={{ color: "#94a3b8" }}>
          {total === 0 ? "No validation messages." : "No rows match the current filter."}
        </p>
      ) : (
        <Grid data={filteredRows}>
          <GridColumn field="errorId" title="#" width="80px" />
          <GridColumn field="severity" title="Sev" width="80px" />
          <GridColumn field="dealItemId" title="Item" width="100px" />
          <GridColumn field="actionEvent" title="Event" width="160px" />
          <GridColumn field="message" title="Message" />
          <GridColumn title="Reference" width="160px" cells={{ data: ReferenceCell }} />
        </Grid>
      )}
    </div>
  );
};

export default DealErrorsPage;
